import React, { useState } from 'react'
import Modal from '@embracesbs/component-modal'
import Text from '@embracesbs/component-text'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import TextField from '@embracesbs/component-textfield/TextField'
import { Subscription } from '../../Interfaces/Types'
import { useApi } from '../../Hooks'

interface DeleteSubscriptionModalProps {
  isOpen: boolean
  subscription: Subscription
  onClosed: () => void
  onDeleted: () => void
}

const DeleteSubscriptionModal: React.FC<DeleteSubscriptionModalProps> = ({ isOpen, subscription, onClosed, onDeleted }) => {
  const { fetchApi } = useApi()
  const [typedName, setTypedName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const nameMatches = typedName.trim().toLowerCase() === subscription.customerName.trim().toLowerCase()

  // The API returns its refusal as a plain-text body naming the subscription and its tenant count.
  const apiMessage = (error: any): string =>
    typeof error?.data === 'string' && error.data.length > 0 ? error.data : 'Something went wrong while deleting the subscription.'

  const handleDelete = async () => {
    setIsDeleting(true)
    setErrorMessage('')
    try {
      const result = await fetchApi({
        endpoint: `/api/subscription/${subscription.id}`,
        method: 'DELETE'
      })
      // fetchApi resolves undefined without sending the request when there is no access token.
      if (result === undefined) {
        setErrorMessage('You are not signed in. Sign in again and retry.')
        return
      }
      onDeleted()
      onClosed()
    } catch (error: any) {
      // 404: another session already deleted it, so the outcome the user asked for holds.
      if (error?.status === 404) {
        onDeleted()
        onClosed()
        return
      }
      setErrorMessage(apiMessage(error))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      width='50%'
      onClose={() => onClosed()}
      hasHeaderBorder={true}
      headerTitle='Delete subscription'
      height={'40%'}
      onConfirm={handleDelete}
      isConfirmDisabled={!nameMatches || isDeleting}
      variant='prompt'
      labelConfirm='Delete'
    >
      <div style={{ marginBottom: '12px' }}>
        <Text
          textStyle='body'
          content={`This removes '${subscription.customerName}' from the overview. It cannot be undone from the application.`}
        />
      </div>
      <TextFieldWrapper labelText='Type the subscription name to confirm'>
        <TextField
          placeholder={subscription.customerName}
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          errorMessage={errorMessage}
          hasAnError={errorMessage !== ''}
          onClear={() => setTypedName('')}
        />
      </TextFieldWrapper>
    </Modal>
  )
}

export default DeleteSubscriptionModal
